import api from '@/services/api'
import { documentoService } from '@/services/documentoService'
import type {
  ArquivoInformativo,
  CodigoErroInformativo,
  MotivoDocumentoInvalidoInformativo,
  InformativoProdutoDetalhe,
  InformativoVersao,
  InformativoVigente,
  PublicarInformativoRequest,
  RetirarInformativoRequest,
} from '@/types/informativo'

/**
 * PDF informativo vigente do produto (Onda 5 — AN-11 §1, L16). Substitui o antigo
 * `manualProdutoService`, que escolhia "o primeiro MANUAL" da listagem de documentos — sem
 * ordem no back-office e com versões antigas visíveis ao consultor. Agora quem diz qual é o
 * vigente é o servidor de documentos (tabela `informativos_produto`), e o consultor só
 * consegue ler o arquivo vigente (`DocumentoAcessoPolicy`).
 *
 * Contrato REAL do DEV-T (documentos, sha 5f2ad3c). Upload de MANUAL não publica sozinho:
 * o PDF só vira informativo pelo POST de publicação.
 */

const BASE_URL = '/api/v1/documentos'
/** O upload do PDF continua como documento de PRODUTO, tipo MANUAL (AN-11 §1.4). */
export const TIPO_ENTIDADE_PRODUTO = 'PRODUTO'
export const TIPO_DOCUMENTO_INFORMATIVO = 'MANUAL'

export const informativoProdutoService = {
  /**
   * Vigentes de todo o catálogo numa chamada (lista do consultor e aba Informativo dele),
   * indexados pelo id do produto. Escopo: catálogo global (Q34); consultor sem escopo válido
   * recebe lista vazia do servidor.
   */
  vigentes: async (): Promise<Map<string, InformativoVigente>> => {
    const lista = await api.get<InformativoVigente[]>(`${BASE_URL}/informativos-vigentes`)
    const porProduto = new Map<string, InformativoVigente>()
    for (const i of (lista as unknown as InformativoVigente[] | null) ?? []) {
      if (i?.produtoId && i.documentoId) porProduto.set(i.produtoId, i)
    }
    return porProduto
  },

  /** Vigente + histórico de um produto — só back-office (consultor recebe 403). */
  doProduto: async (produtoId: string): Promise<InformativoProdutoDetalhe> => {
    const r = (await api.get<InformativoProdutoDetalhe>(
      `${BASE_URL}/informativos/produto/${produtoId}`,
    )) as unknown as InformativoProdutoDetalhe | null
    return { vigente: r?.vigente ?? null, historico: r?.historico ?? [] }
  },

  /** Publica um documento já enviado como o vigente (ADMIN). 201 nova linha; 200 se já era o vigente. */
  publicar: async (produtoId: string, request: PublicarInformativoRequest): Promise<InformativoVersao> =>
    (await api.post<InformativoVersao>(
      `${BASE_URL}/informativos/produto/${produtoId}`,
      request,
    )) as unknown as InformativoVersao,

  /** Encerra o vigente com RETIRADO (ADMIN; motivo ≤500) — 404 sem vigente. */
  retirar: async (produtoId: string, request: RetirarInformativoRequest): Promise<void> => {
    await api.post(`${BASE_URL}/informativos/produto/${produtoId}/retirar`, request)
  },

  /**
   * Envia o PDF como documento do produto e o publica. Devolve o `documentoId` enviado mesmo
   * quando a publicação falha (`erro`): o arquivo recém-enviado pode estar em quarentena
   * (409 `INFORMATIVO_EM_QUARENTENA`) e a tela tenta publicar o MESMO documento depois, sem
   * novo upload.
   */
  enviarEPublicar: async (
    produtoId: string,
    arquivo: File,
    observacao?: string | null,
  ): Promise<{ documentoId: string; publicado: InformativoVersao | null; erro: unknown }> => {
    const enviado = await documentoService.upload(
      arquivo,
      TIPO_DOCUMENTO_INFORMATIVO,
      produtoId,
      TIPO_ENTIDADE_PRODUTO,
    )
    try {
      const publicado = await informativoProdutoService.publicar(produtoId, {
        documentoId: enviado.id,
        observacao: observacao?.trim() || null,
      })
      return { documentoId: enviado.id, publicado, erro: null }
    } catch (erro) {
      return { documentoId: enviado.id, publicado: null, erro }
    }
  },

  /**
   * Baixa os bytes pelo backend (`/arquivo`, auditado). Âncora com `download` em vez de
   * window.open depois de um await: o Safari bloqueia pop-up que não nasce do clique
   * (Q-AN11-3 provisória: "Abrir" baixa).
   */
  baixar: async (arquivo: ArquivoInformativo): Promise<void> => {
    const blob = await documentoService.baixarArquivo(arquivo.documentoId)
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = arquivo.nome || 'informativo-do-produto.pdf'
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(url), 0)
  },
}

// ─── Mensagens dos códigos de erro (AN-11 §1.2–1.3) ───

const TEXTO_ERRO: Record<CodigoErroInformativo, string> = {
  INFORMATIVO_DOCUMENTO_INVALIDO:
    'Este arquivo não pode ser o informativo do produto: precisa ser um PDF enviado para este produto.',
  INFORMATIVO_EM_QUARENTENA:
    'O PDF foi enviado e ainda está na verificação de segurança. Aguarde alguns instantes e publique de novo.',
  INFORMATIVO_CONFLITO_CONCORRENCIA:
    'Outra pessoa publicou o informativo deste produto ao mesmo tempo. Recarregue e confira antes de publicar de novo.',
  DOCUMENTO_E_INFORMATIVO_VIGENTE:
    'Este arquivo é o informativo vigente de um produto. Retire ou substitua o informativo antes de remover o arquivo.',
}

/** `data.motivo` do 422 `INFORMATIVO_DOCUMENTO_INVALIDO`, em texto. */
const TEXTO_MOTIVO_INVALIDO: Record<MotivoDocumentoInvalidoInformativo, string> = {
  DOCUMENTO_INEXISTENTE: 'o arquivo enviado não foi encontrado',
  NAO_E_DE_PRODUTO: 'o arquivo não foi enviado como documento de produto',
  OUTRO_PRODUTO: 'o arquivo foi enviado para outro produto',
  NAO_E_PDF: 'o arquivo não é um PDF',
}

/** O motivo pode vir em `data.motivo` do envelope ApiResponse (corpo do erro). */
const motivoInvalido = (e: unknown): string | undefined => {
  const corpo = (e as { data?: { data?: { motivo?: unknown }; motivo?: unknown } } | null)?.data
  const m = corpo?.data?.motivo ?? corpo?.motivo
  return typeof m === 'string' ? TEXTO_MOTIVO_INVALIDO[m as MotivoDocumentoInvalidoInformativo] : undefined
}

export const codigoErroInformativo = (e: unknown): string | undefined => {
  const data = (e as { data?: { errorCode?: unknown } } | null)?.data
  return typeof data?.errorCode === 'string' ? data.errorCode : undefined
}

/** Texto legível; a mensagem do backend (motivo da recusa) vai junto quando acrescenta algo. */
export const mensagemErroInformativo = (e: unknown, padrao: string): string => {
  const codigo = codigoErroInformativo(e)
  const status = (e as { status?: number } | null)?.status
  const backend = (e as { message?: unknown } | null)?.message
  const doBackend = typeof backend === 'string' && backend.trim() ? backend : undefined
  if (codigo === 'INFORMATIVO_DOCUMENTO_INVALIDO') {
    const motivo = motivoInvalido(e)
    if (motivo) return `${TEXTO_ERRO.INFORMATIVO_DOCUMENTO_INVALIDO} Motivo: ${motivo}.`
  }
  if (codigo && codigo in TEXTO_ERRO) {
    const texto = TEXTO_ERRO[codigo as CodigoErroInformativo]
    return doBackend && doBackend !== texto ? `${texto} (${doBackend})` : texto
  }
  if (status === 403) return 'Só o administrador publica ou retira o informativo do produto.'
  return doBackend ?? padrao
}
